import { toBusinessDate } from '@bssb/utils';
import { createFileRoute } from '@tanstack/react-router';
import { ShareIcon } from 'lucide-react';
import { useState } from 'react';
import { BarChart, type ChartPoint } from '@/components/mini-chart';
import { ListSkeleton } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Card, CardBody, DataRow, SectionTitle } from '@/components/ui/card';
import { Alert, Field, Input } from '@/components/ui/form';
import { useAuth } from '@/lib/auth/context';
import { dateId, integer, rupiah } from '@/lib/format';
import { type DailyStats, dailyStats, statsSeries } from '@/offline/queries';
import { useOfflineQuery } from '@/offline/use-offline-query';

export const Route = createFileRoute('/_app/laporan')({
  component: ReportPage,
});

const DAY_LABELS = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];

/**
 * Laporan harian pribadi (T-P7-26, FR-REPORT-01..04/09).
 *
 * Dihitung dari data di perangkat, bukan ditarik dari server: laporan yang hanya
 * bisa dibuka saat online tidak berguna bagi orang yang menyusunnya di jalan
 * pulang. Konsekuensinya disebutkan terang-terangan di layar — angka ini
 * mencakup kunjungan yang belum terkirim, sehingga bisa berbeda dari rekap
 * supervisor sampai antrean kosong.
 */
function ReportPage() {
  const { user } = useAuth();
  const [date, setDate] = useState(toBusinessDate(new Date()));

  const { data: stats, loading } = useOfflineQuery(
    () => dailyStats(new Date(`${date}T00:00:00`)),
    [date],
  );
  const { data: series } = useOfflineQuery(() => statsSeries(7), []);

  const chartPoints: ChartPoint[] = (series ?? []).map((entry) => {
    const day = new Date(`${entry.date}T00:00:00`);
    return {
      label: DAY_LABELS[day.getDay()] ?? '',
      value: entry.paidAmount,
      description: `${dateId(entry.date)}: ${rupiah(entry.paidAmount)} dari ${entry.paid} pembayaran`,
    };
  });

  return (
    <div className="space-y-4 px-4 py-4">
      <Field id="report-date" label="Tanggal laporan">
        <Input
          id="report-date"
          type="date"
          max={toBusinessDate(new Date())}
          value={date}
          onChange={(event) => setDate(event.target.value)}
        />
      </Field>

      {loading || !stats ? (
        <ListSkeleton rows={2} />
      ) : (
        <>
          <Card>
            <CardBody>
              <dl className="divide-y divide-border">
                <DataRow label="Kunjungan" value={integer(stats.visits)} />
                <DataRow label="Berhasil ditagih" value={integer(stats.paid)} />
                <DataRow label="Total setoran" value={rupiah(stats.paidAmount)} />
                <DataRow label="Janji bayar" value={integer(stats.ptp)} />
                <DataRow label="Nilai janji bayar" value={rupiah(stats.ptpAmount)} />
                <DataRow label="Tidak berhasil" value={integer(stats.failed)} />
              </dl>
            </CardBody>
          </Card>

          {stats.pendingSync > 0 ? (
            <Alert variant="warning">
              {integer(stats.pendingSync)} item masih menunggu kirim. Angka di atas sudah
              memasukkannya, sehingga bisa berbeda dari rekap supervisor sampai antrean kosong.
            </Alert>
          ) : null}

          <Button
            variant="secondary"
            fullWidth
            leading={<ShareIcon className="size-4" aria-hidden="true" />}
            onClick={() => void shareReport(stats, user?.fullName ?? '')}
          >
            Bagikan laporan
          </Button>

          <section className="space-y-2">
            <SectionTitle>Setoran 7 hari terakhir</SectionTitle>
            <Card>
              <CardBody>
                <BarChart points={chartPoints} />
              </CardBody>
            </Card>
          </section>
        </>
      )}
    </div>
  );
}

async function shareReport(stats: DailyStats, collectorName: string): Promise<void> {
  const text = [
    `LAPORAN HARIAN — ${dateId(stats.date)}`,
    `Collector: ${collectorName}`,
    '',
    `Kunjungan       : ${stats.visits}`,
    `Berhasil ditagih: ${stats.paid}`,
    `Total setoran   : ${rupiah(stats.paidAmount)}`,
    `Janji bayar     : ${stats.ptp} (${rupiah(stats.ptpAmount)})`,
    `Tidak berhasil  : ${stats.failed}`,
    '',
    stats.pendingSync > 0
      ? `Catatan: ${stats.pendingSync} item belum tersinkron ke sistem.`
      : 'Seluruh data sudah tersinkron.',
  ].join('\n');

  if (navigator.share) {
    await navigator.share({ title: 'Laporan harian', text }).catch(() => undefined);
    return;
  }

  await navigator.clipboard.writeText(text).catch(() => undefined);
}
